import { useTranslation } from "react-i18next";
import Actions from "../../../Actions";
import { isVideo } from "../../SelfServiceCampaignCreation/Previews/utils";
import { processAutocroppedImage } from "../../../utils/processAutocroppedImage";
import { hasParamsAlready } from "../../SelfServiceCampaignCreation/Previews/media-assets-lab/layout/Layout";
import { useAdUpdate } from "./useAdUpdate";
import { getVideoDuration } from "../../SelfServiceCampaignCreation/Previews/TemplatePost/TemplatePost__Preview/utils";

export const useUpdateCombinationData = ({
  currentAd,
  userProfile,
  dimensions,
  allAdCombinations
}) => {
  const { t } = useTranslation();
  const { updateMediaAssets } = useAdUpdate(currentAd, allAdCombinations, userProfile);
  const mediaAssets = currentAd?.mediaAssets;

  const updateCombinationData = async ({ newAsset, orientation = null }) => {
    const isNewAssetVideo = isVideo(newAsset);
    const mediaAssetType = isNewAssetVideo ? "video" : "image";
    const assetKey = isNewAssetVideo ? "video" : "backgroundImage";
    const oldAssetKey = isNewAssetVideo ? "backgroundImage" : "video";
    const currentTemplateId = currentAd?.mediaAssets?.vertical?.templateId || "";

    let updatedTemplateId = currentTemplateId;

    if (currentTemplateId.includes("Image") && isNewAssetVideo) {
      updatedTemplateId = currentTemplateId.replace("Image", "Video");
    } else if (currentTemplateId.includes("Video") && !isNewAssetVideo) {
      updatedTemplateId = currentTemplateId.replace("Video", "Image");
    }

    if (!updatedTemplateId) {
      Actions.addNotification(t("Template not found"), "error");
      return;
    }

    // Determine if the new asset already has a Cloudinary parameter
    const hasCloudinaryParam = hasParamsAlready.test(newAsset);

    // Check if the new asset is the same as the current asset for the given orientation
    const currentAsset = mediaAssets[orientation]?.inputProps[assetKey];
    if (currentAsset === newAsset) {
      return; // Return early if the new asset is the same
    }

    const updateSingleOrientation = async (orientationKey) => {
      const duration = isNewAssetVideo ? await getVideoDuration(newAsset) : null;

      const updatedOrientationMedia = {
        ...mediaAssets[orientationKey],
        templateId: updatedTemplateId,
        type: mediaAssetType,
        inputProps: {
          ...mediaAssets[orientationKey].inputProps,
          duration,
          [oldAssetKey]: "",
          [assetKey]:
            hasCloudinaryParam || isNewAssetVideo
              ? newAsset
              : await processAutocroppedImage(
                  newAsset,
                  orientationKey,
                  dimensions,
                  updatedTemplateId
                )
        }
      };
      return updatedOrientationMedia;
    };

    let updatedMediaAssets;

    if (orientation) {
      // If orientation is provided, update only that orientation
      updatedMediaAssets = {
        ...mediaAssets,
        [orientation]: await updateSingleOrientation(orientation)
      };
    } else {
      // If no orientation is provided, update all orientations
      updatedMediaAssets = {
        ...mediaAssets,
        vertical: await updateSingleOrientation("vertical"),
        portrait: await updateSingleOrientation("portrait"),
        landscape: await updateSingleOrientation("landscape")
      };
    }

    updateMediaAssets({ updatedMediaAssets });
  };

  return { updateCombinationData };
};
